import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Button, Card, Grid, Header, Icon, Image, Label, Loader, Message, Segment, Tab,
} from 'semantic-ui-react';
import { useStore } from '../../app/stores/store';
import { useTranslation } from 'react-i18next';
import { MyTournamentNotificationDto, TournamentSearchResultDto } from '../../app/models/Tournament';
import { Link } from 'react-router-dom';
import { TournamentSearchPage } from './TournamentSearchPage';

// ── Notification banner ───────────────────────────────────────────────────────

const TournamentNotificationBanner = observer(function TournamentNotificationBanner() {
  const { tournamentStore } = useStore();
  const { t } = useTranslation();
  const notifications = tournamentStore.myNotifications;

  if (notifications.length === 0) return null;

  const iconForType = (type: string) => {
    if (type === 'RefereeInvite') return 'gavel';
    if (type === 'RefereePendingApproval') return 'clock';
    return 'users';
  };

  const colorForType = (type: string): 'yellow' | 'orange' | 'blue' => {
    if (type === 'RefereeInvite') return 'yellow';
    if (type === 'RefereePendingApproval') return 'orange';
    return 'blue';
  };

  const descriptionForType = (n: MyTournamentNotificationDto) => {
    if (n.type === 'RefereeInvite') return t('tournament.notifications.refereeInviteDesc');
    if (n.type === 'RefereePendingApproval') return t('tournament.notifications.refereePendingDesc');
    return t('tournament.notifications.pendingRegistrationsDesc', { count: n.count });
  };

  const tabForType = (type: string) => {
    if (type === 'RefereeInvite' || type === 'RefereePendingApproval') return 'referees';
    return 'admin';
  };

  return (
    <Segment style={{ marginBottom: 16, padding: '12px 16px' }}>
      <Header as="h5" style={{ margin: '0 0 10px 0' }}>
        <Icon name="bell" color="orange" />
        {t('tournament.notifications.title')}
        <Label circular color="orange" size="mini" style={{ marginLeft: 8, verticalAlign: 'middle' }}>
          {notifications.length}
        </Label>
      </Header>
      {notifications.map((n, i) => (
        <Message
          key={`${n.tournamentId}-${n.type}-${i}`}
          color={colorForType(n.type)}
          style={{ marginBottom: 8, padding: '10px 14px' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Image
                src={n.tournamentLogoUrl || '/assets/tournament-placeholder.png'}
                avatar
                style={{ width: 36, height: 36, objectFit: 'cover', flexShrink: 0 }}
              />
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.95em' }}>{n.tournamentName}</div>
                <div style={{ fontSize: '0.82em', color: '#555', marginTop: 2 }}>
                  <Icon name={iconForType(n.type) as any} size="small" />
                  {descriptionForType(n)}
                </div>
              </div>
            </div>

            {/* RefereeInvite — Accept/Decline with per-notification keys */}
            {n.type === 'RefereeInvite' && (
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <Button
                  positive size="small"
                  loading={tournamentStore.isSubmitting(`referee:respond:${n.tournamentId}:accept`)}
                  disabled={tournamentStore.submitting}
                  onClick={() => tournamentStore.respondRefereeInvite(n.tournamentId, true)}
                >
                  <Icon name="check" />{t('tournament.notifications.accept')}
                </Button>
                <Button
                  negative basic size="small"
                  loading={tournamentStore.isSubmitting(`referee:respond:${n.tournamentId}:decline`)}
                  disabled={tournamentStore.submitting}
                  onClick={() => tournamentStore.respondRefereeInvite(n.tournamentId, false)}
                >
                  <Icon name="close" />{t('tournament.notifications.decline')}
                </Button>
              </div>
            )}

            {/* Other notifications — link to tournament */}
            {n.type !== 'RefereeInvite' && (
              <Button
                size="mini"
                color={colorForType(n.type)}
                as={Link}
                to={`/tournaments/${n.tournamentId}?tab=${tabForType(n.type)}`}
              >
                <Icon name="arrow right" />
                {n.type === 'RefereePendingApproval'
                  ? t('tournament.notifications.refereePending')
                  : t('tournament.notifications.pendingRegistrations', { count: n.count })}
              </Button>
            )}
          </div>
        </Message>
      ))}
    </Segment>
  );
});

// ── Main Dashboard ────────────────────────────────────────────────────────────

export const TournamentDashboard = observer(function TournamentDashboard() {
  const { tournamentStore } = useStore();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    tournamentStore.myOrganized = [];
    tournamentStore.myParticipating = [];
    tournamentStore.loadMyOrganized();
    tournamentStore.loadMyParticipating();
    tournamentStore.loadMyNotifications();
  }, []);

  const StatusLabel = ({ status }: { status: string }) => {
    const colors: Record<string, string> = {
      Draft: 'grey', Published: 'blue', RegistrationOpen: 'green',
      RegistrationClosed: 'orange', InProgress: 'teal', Completed: 'purple', Cancelled: 'red',
    };
    return <Label size="tiny" color={colors[status] as any}>{t(`tournament.status.${status.toLowerCase()}`)}</Label>;
  };

  const TournamentCard = ({ t_ }: { t_: TournamentSearchResultDto }) => (
    <Card as={Link} to={`/tournaments/${t_.id}`}>
      <Card.Content>
        <img
          src={t_.logoUrl || '/assets/tournament-placeholder.png'}
          alt="logo"
          style={{ float: 'right', width: 44, height: 44, objectFit: 'cover', borderRadius: 4 }}
        />
        <Card.Header>{t_.name}</Card.Header>
        <Card.Meta>{t(`sports.${t_.sportName.toLowerCase()}`) || t_.sportName}</Card.Meta>
        <Card.Description>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            <StatusLabel status={t_.status} />
            <Label size="tiny" basic>
              <Icon name="calendar" />{new Date(t_.startsAt).toLocaleDateString()}
            </Label>
            <Label size="tiny" basic>
              <Icon name="users" />{t_.registeredTeamsCount}/{t_.maxTeams}
            </Label>
            {t_.myRegistrationStatus === 'Pending' && (
              <Label size="tiny" color="yellow" basic>
                <Icon name="clock" />{t('tournament.status.pendingApproval')}
              </Label>
            )}
            {t_.isOnline && <Label size="tiny" color="blue" basic><Icon name="wifi" />Online</Label>}
          </div>
        </Card.Description>
      </Card.Content>
      <Card.Content extra style={{ fontSize: '0.82em', color: '#888' }}>
        <Icon name="user" />{t_.organizerName}
        {t_.entryFee === 0 && <span style={{ marginLeft: 10 }}>🆓 Free</span>}
        {(t_.entryFee ?? 0) > 0 && <span style={{ marginLeft: 10 }}>€{t_.entryFee}</span>}
      </Card.Content>
    </Card>
  );

  const panes = [
    { menuItem: { key: 'organized', content: t('tournament.dashboard.organized'), icon: 'trophy' } },
    { menuItem: { key: 'participating', content: t('tournament.dashboard.participating'), icon: 'shield' } },
    { menuItem: { key: 'find', content: t('tournament.dashboard.find'), icon: 'search' } },
  ];

  return (
    <Grid stackable>
      <Grid.Column width={16}>
        <Segment
          color="teal"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}
        >
          <Header as="h2" style={{ margin: 0 }}>
            <Icon name="trophy" color="teal" />
            <Header.Content>
              {t('tournament.dashboard.title')}
              <Header.Subheader>{t('tournament.dashboard.subtitle')}</Header.Subheader>
            </Header.Content>
          </Header>
          <Button primary as={Link} to="/createTournament">
            <Icon name="plus" />{t('tournament.dashboard.create')}
          </Button>
        </Segment>

        <TournamentNotificationBanner />

        <Tab
          menu={{ secondary: true, pointing: true }}
          panes={panes}
          activeIndex={activeTab}
          onTabChange={(_: any, d: any) => setActiveTab(d.activeIndex as number)}
        />

        {activeTab === 0 && (
          <Segment basic>
            {tournamentStore.loading ? (
              <Loader active inline="centered" style={{ margin: 30 }} />
            ) : tournamentStore.myOrganized.length === 0 ? (
              <Message info>
                <Icon name="info circle" />{t('tournament.dashboard.noOrganized')}
              </Message>
            ) : (
              <Card.Group stackable itemsPerRow={3}>
                {tournamentStore.myOrganized.map(t_ => <TournamentCard key={t_.id} t_={t_} />)}
              </Card.Group>
            )}
          </Segment>
        )}

        {activeTab === 1 && (
          <Segment basic>
            {tournamentStore.loading ? (
              <Loader active inline="centered" style={{ margin: 30 }} />
            ) : tournamentStore.myParticipating.length === 0 ? (
              <Message info>
                <Icon name="info circle" />{t('tournament.dashboard.noParticipating')}
              </Message>
            ) : (
              <Card.Group stackable itemsPerRow={3}>
                {tournamentStore.myParticipating.map(t_ => <TournamentCard key={t_.id} t_={t_} />)}
              </Card.Group>
            )}
          </Segment>
        )}

        {activeTab === 2 && (
          <Segment basic>
            <TournamentSearchPage embedded />
          </Segment>
        )}
      </Grid.Column>
    </Grid>
  );
});